(function () {
  'use strict';

  // Utility helpers
  const escapeHTML = (str) => {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const toCents = (val) => {
    if (val == null) return null;
    const num = parseFloat(val);
    if (Number.isNaN(num)) return null;
    return Math.round(num * 100);
  };

  const formatMoney = (cents, currencyCode) => {
    try {
      if (typeof window !== 'undefined' && window.theme) {
        if (theme.Shopify && typeof theme.Shopify.formatMoney === 'function') {
          return `<span class="theme-money">${theme.Shopify.formatMoney(cents, theme.money_format_with_code_preference)}</span>`;
        }
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return `<span class="theme-money">${window.Shopify.formatMoney(cents, theme.money_format_with_code_preference)}</span>`;
        }
      }
    } catch (e) {}
    try {
      const amount = (cents || 0) / 100;
      const nf = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      });
      return `<span class="theme-money">${nf.format(amount)}</span>`;
    } catch (e) {
      const amount = ((cents || 0) / 100).toFixed(2);
      const sym = currencyCode === 'USD' ? '$' : '';
      return `<span class="theme-money">${sym}${amount}</span>`;
    }
  };

  const pricesFromProduct = (p) => {
    const priceCents = toCents(p?.priceInfo?.price);
    const originalCents = toCents(p?.priceInfo?.originalPrice);
    const onSale =
      typeof priceCents === 'number' &&
      typeof originalCents === 'number' &&
      originalCents > priceCents;
    return { priceCents, originalCents, onSale };
  };

  const variantHasAvailabilityData = (variant) => {
    if (!variant) return false;
    if (variant.availableForSale != null || variant.available != null) return true;
    if (variant.inventoryPolicy != null || variant.inventory_policy != null) return true;
    const inventoryValue =
      variant.inventoryQuantity ??
      variant.inventory_quantity ??
      variant.inventory ??
      variant.quantity;
    return inventoryValue != null;
  };

  const isSoldOut = (p) => {
    const variants = Array.isArray(p?.variants) ? p.variants : [];
    if (variants.some(variantHasAvailabilityData)) {
      return !variants.some((variant) => isVariantAvailable(variant));
    }
    // No per-variant availability signals — fall back to the product level,
    // where isVariantAvailable's unknown-means-available default would mask
    // a totalInventory of 0 (same order as rcc-badges.js)
    if (typeof p?.totalInventory === 'number') {
      return p.totalInventory <= 0;
    }
    if (typeof p?.availableForSale === 'boolean') return !p.availableForSale;
    if (typeof p?.available === 'boolean') return !p.available;
    return false;
  };

  const productHandleFromUrl = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (e) {
      const match = String(url).match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }
  };

  const normalizeShopifyId = (value) => {
    if (value == null) return '';
    const stringValue = String(value);
    const gidMatch = stringValue.match(/\/(\d+)$/);
    if (gidMatch) return gidMatch[1];
    const numericMatch = stringValue.match(/^\d+$/);
    return numericMatch ? numericMatch[0] : '';
  };

  const variantIdFromVariant = (variant) => {
    if (!variant) return '';
    return normalizeShopifyId(
      variant.id ||
        variant.variantId ||
        variant.legacyResourceId ||
        variant.legacy_resource_id ||
        variant.admin_graphql_api_id
    );
  };

  const getDirectAddVariant = (p) => {
    const variants = Array.isArray(p?.variants) ? p.variants : [];
    if (!variants.length) return null;

    if (variants.length === 1) {
      const variant = variants[0];
      const id = variantIdFromVariant(variant);
      if (id && isVariantAvailable(variant)) return { id, variant };
    }

    return null;
  };

  const isVariantAvailable = (variant) => {
    if (!variant) return false;
    if (variant.availableForSale === false || variant.available === false) return false;
    // Explicit availability flags win over inventory counts — a variant can
    // have 0 on hand yet be purchasable (continue-selling)
    if (variant.availableForSale === true || variant.available === true) return true;
    if (variant.inventoryPolicy === 'CONTINUE' || variant.inventory_policy === 'continue') return true;

    const inventoryValue =
      variant.inventoryQuantity ??
      variant.inventory_quantity ??
      variant.inventory ??
      variant.quantity;

    if (typeof inventoryValue === 'number') return inventoryValue > 0;
    if (typeof inventoryValue === 'string' && inventoryValue.trim() !== '') {
      const parsed = Number(inventoryValue);
      if (Number.isFinite(parsed)) return parsed > 0;
    }

    return inventoryValue == null;
  };

  const getProductId = (p) => {
    return normalizeShopifyId(p?.id || p?.productId || p?.legacyResourceId || p?.legacy_resource_id) || String(p?.id || '');
  };

  const getProductSku = (p, variant) => {
    return variant?.sku || p?.sku || p?.gtin || '';
  };

  const getCollectionListContext = () => {
    const configured = window.SuayCollectionList || {};
    const pathHandle = window.location.pathname.split('/collections/')[1]?.split('/')[0] || '';
    const title =
      configured.name ||
      document.querySelector('.collection-hero__title')?.textContent?.trim() ||
      document.title.replace(/\s*[|–-]\s*.*$/, '').trim() ||
      'Collection';

    return {
      id: configured.id || (pathHandle ? `collection_${pathHandle}` : 'collection'),
      name: title,
      handle: configured.handle || pathHandle
    };
  };

  const isCollectionPage = () => {
    return !!window.SuayCollectionList || window.location.pathname.indexOf('/collections/') !== -1;
  };

  const isPredictiveSearchElement = (element) => {
    return !!element?.closest('[role="combobox"], predictive-search, .predictive-search, app-autocomplete');
  };

  const isCollectionRccGridElement = (element) => {
    if (!isCollectionPage() || !element || isPredictiveSearchElement(element)) return false;
    const rccGrid = element.closest('.rcc-search');
    return !!(rccGrid && rccGrid.closest('.shopify-app-block'));
  };

  const normalizeIndex = (value, fallbackElement) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    if (Number.isFinite(parsed) && parsed === 0) return 1;

    const productElements = Array.from(document.querySelectorAll('rcc-suay-product:not([hidden])'));
    const fallbackIndex = productElements.indexOf(fallbackElement);
    return fallbackIndex >= 0 ? fallbackIndex + 1 : undefined;
  };

  const productItem = (p, { variant, index, listContext } = {}) => {
    const prices = pricesFromProduct(p);
    const item = {
      item_id: getProductSku(p, variant) || getProductId(p) || p?.handle || p?.url || p?.title || '',
      item_name: p?.title || '',
      item_list_id: listContext?.id,
      item_list_name: listContext?.name,
      index,
      price: typeof prices.priceCents === 'number' ? prices.priceCents / 100 : undefined
    };

    if (variant) {
      item.item_variant = variant.title || variant.name || [variant.option1, variant.option2, variant.option3].filter(Boolean).join(' / ');
    }
    if (p?.brand) item.item_brand = p.brand;
    if (p?.type || p?.productType) item.item_category = p.type || p.productType;

    Object.keys(item).forEach((key) => {
      if (item[key] == null || item[key] === '') delete item[key];
    });

    return item;
  };

  // Google & YouTube's tag runs in the top frame and reads this dataLayer, so
  // a gtag('event') here reaches GA4 too. It already sends Shopify's standard
  // events (page_view, view_item_list, view_item, add_to_cart, ...) itself;
  // send only events it doesn't, or the reports count them twice.
  const pushEcommerceEvent = (eventName, payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(Object.assign({ event: eventName }, payload));

    if (typeof window.gtag === 'function') {
      // gtag expects items/currency/value as flat event params, not nested under ecommerce
      const gtagParams = Object.assign({}, payload, payload.ecommerce || {});
      delete gtagParams.ecommerce;
      window.gtag('event', eventName, gtagParams);
    }
  };

  // add_to_cart itself comes from the Google & YouTube app; this custom event
  // only records which list the add came from.
  const pushQuickAddEvent = (params) => {
    const payload = Object.assign({}, params);
    Object.keys(payload).forEach((key) => {
      if (payload[key] == null || payload[key] === '') delete payload[key];
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: 'quick_add' }, payload));
    if (typeof window.gtag === 'function') window.gtag('event', 'quick_add', payload);
  };

  const trackSelectItem = (element) => {
    const card = element?.closest('rcc-suay-product');
    const product = card?.product;
    if (!card || !product || !isCollectionRccGridElement(card)) return;

    const listContext = getCollectionListContext();
    const index = normalizeIndex(card.getAttribute('loop-index') || product.index, card);
    const directVariant = getDirectAddVariant(product);
    const item = productItem(product, {
      variant: directVariant?.variant,
      index,
      listContext
    });

    pushEcommerceEvent('select_item', {
      item_list_id: listContext.id,
      item_list_name: listContext.name,
      ecommerce: {
        item_list_id: listContext.id,
        item_list_name: listContext.name,
        items: [item]
      }
    });
  };

  const renderCartUpdate = (data, triggerElement) => {
    const normalizedData = normalizeCartAddResponse(data);
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cart && typeof cart.renderContents === 'function') {
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(triggerElement || document.activeElement);
      cart.renderContents(normalizedData);
      // Dawn's renderContents clears is-empty on .drawer__inner but the host
      // class is normally cleared by product-form.js, which this path bypasses
      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      return;
    }

    if (renderCartBubbleSection(normalizedData?.sections?.['cart-icon-bubble'])) return;

    const itemCount = normalizedData?.item_count;
    if (typeof itemCount !== 'number') {
      refreshCartCount();
      return;
    }
    document.querySelectorAll('[data-cart-count], .header__cart-count, #cart-icon-bubble .suay-header__cart-badge').forEach((element) => {
      element.textContent = itemCount;
    });
  };

  const normalizeCartAddResponse = (data) => {
    if (!data || data.key || !Array.isArray(data.items) || !data.items[0]) return data;
    return Object.assign({}, data.items[0], {
      items: data.items,
      sections: data.sections
    });
  };

  const renderCartBubbleSection = (sectionHtml) => {
    if (!sectionHtml) return false;
    const current = document.getElementById('cart-icon-bubble');
    if (!current) return false;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const replacement = parsed.getElementById('cart-icon-bubble');
    if (replacement) {
      current.replaceWith(replacement);
      return true;
    }

    // The section render wraps its markup in a .shopify-section div with no
    // #cart-icon-bubble inside; mirror Dawn's getSectionInnerHTML swap
    const sectionWrapper = parsed.querySelector('.shopify-section') || parsed.body;
    if (!sectionWrapper || !sectionWrapper.innerHTML.trim()) return false;
    current.innerHTML = sectionWrapper.innerHTML;
    return true;
  };

  const refreshCartCount = () => {
    fetch('/cart.json')
      .then((response) => response.json())
      .then((cart) => {
        if (typeof cart?.item_count !== 'number') return;
        document.querySelectorAll('[data-cart-count], .header__cart-count, #cart-icon-bubble .suay-header__cart-badge').forEach((element) => {
          element.textContent = cart.item_count;
        });
      })
      .catch(() => null);
  };

  const addDirectVariantToCart = (button) => {
    const card = button.closest('rcc-suay-product');
    const product = card?.product;
    const variantId = button.getAttribute('data-rcc-variant-id');
    if (!card || !product || !variantId || button.getAttribute('aria-disabled') === 'true') return;

    const previousText = button.textContent;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const sections = cart && typeof cart.getSectionsToRender === 'function'
      ? cart.getSectionsToRender().map((section) => section.id)
      : ['cart-icon-bubble'];

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    button.textContent = 'Adding...';

    fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections,
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json().then((data) => {
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not add to cart');
        }
        return data;
      }))
      .then((data) => {
        const listContext = getCollectionListContext();
        const index = normalizeIndex(card.getAttribute('loop-index') || product.index, card);
        const directVariant = getDirectAddVariant(product);
        const item = productItem(product, {
          variant: directVariant?.variant,
          index,
          listContext
        });
        item.quantity = 1;

        pushQuickAddEvent({
          quick_add_source: 'collection',
          currency: product?.priceInfo?.currencyCode || 'USD',
          value: item.price || undefined,
          item_list_id: listContext.id,
          item_list_name: listContext.name,
          item_id: item.item_id,
          item_name: item.item_name,
          item_variant: item.item_variant
        });

        button.textContent = 'Added';
        renderCartUpdate(data, button);

        window.setTimeout(() => {
          button.textContent = previousText;
        }, 1200);
      })
      .catch((error) => {
        button.textContent = error.message || 'Try again';
        window.setTimeout(() => {
          button.textContent = previousText;
        }, 1800);
      })
      .finally(() => {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  };

  const parsePriceText = (text) => {
    if (!text) return null;
    const match = String(text).replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
    return match ? match[1] : null;
  };

  const variantIdFromHref = (href) => {
    if (!href) return '';
    const match = String(href).match(/[?&]variant=(\d+)/);
    if (match) return match[1];

    try {
      const parsed = new URL(href, window.location.origin);
      return parsed.searchParams.get('variant') || '';
    } catch (e) {
      return '';
    }
  };

  // RCC's card click handler pushes ?page=1&pid=<id> so Back can restore the
  // grid. Our quick actions never leave the grid, so that entry only adds a
  // Back step and a history-change page_view in GA4. The buttons handle their
  // own click and keep it from reaching RCC's card.
  const handleQuickActionClick = (event) => {
    const button = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    const handle = button.getAttribute('data-quick-buy-handle');
    if (handle) {
      if (typeof window.openQuickBuy === 'function') window.openQuickBuy(handle, button);
      return;
    }

    if (button.hasAttribute('data-rcc-quick-add')) {
      trackSelectItem(button);
      addDirectVariantToCart(button);
    }
  };

  const nativeCardData = (card) => {
    if (!card) return null;

    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;

    const href = link.getAttribute('href') || '';
    const url = new URL(href, window.location.origin);
    const handle = productHandleFromUrl(url.href);
    const variantId = variantIdFromHref(href) || variantIdFromHref(url.href);
    const indexAttr = card.getAttribute('data-rcc-product-card-index');
    const parsedIndex = Number(indexAttr);
    const index = Number.isFinite(parsedIndex) ? parsedIndex + 1 : normalizeIndex(indexAttr, card);
    const title =
      (link.getAttribute('aria-label') || '').replace(/\s+-\s+image\s*$/i, '').trim() ||
      card.querySelector('.rcc-search__product__name, .rcc-search__product__title, .rcc-search__product__metafield')?.textContent?.trim() ||
      'Product';
    const priceText =
      card.querySelector('.rcc-search__product__price, .rcc-search__product__price__now, .price')?.textContent?.trim() ||
      '';
    const price = parsePriceText(priceText);

    return {
      product: {
        id: card.id || handle,
        title,
        url: href,
        handle,
        priceInfo: {
          price,
          currencyCode: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
        },
        variants: variantId
          ? [
              {
                id: variantId,
                availableForSale: true
              }
            ]
          : []
      },
      handle,
      variantId,
      index,
      link
    };
  };

  const trackNativeSelectItem = (element) => {
    const card = element?.closest('.rcc-search__product');
    const data = nativeCardData(card);
    if (!data || !isCollectionRccGridElement(card)) return;

    const listContext = getCollectionListContext();
    const item = productItem(data.product, {
      variant: data.product.variants[0],
      index: data.index,
      listContext
    });

    pushEcommerceEvent('select_item', {
      item_list_id: listContext.id,
      item_list_name: listContext.name,
      ecommerce: {
        item_list_id: listContext.id,
        item_list_name: listContext.name,
        items: [item]
      }
    });
  };

  const enhanceNativeRccCard = (card) => {
    if (!isCollectionRccGridElement(card) || !card || card.dataset.suayRccEnhanced === 'true') return;
    if (card.classList.contains('suay-rcc-product--hidden')) return;

    const data = nativeCardData(card);
    if (!data || !data.handle) return;

    card.dataset.suayRccEnhanced = 'true';
    card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.classList.add('suay-rcc-native-product-link');
    });

    const isUnavailable =
      !!card.querySelector('.product-label.unavailable') ||
      (card.textContent && /\bsold\s*out\b|\bout\s*of\s*stock\b/i.test(card.textContent));
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'suay-rcc-quick-add suay-rcc-quick-add--native';
    button.setAttribute('data-rcc-native-quick-add', '');
    button.setAttribute('data-rcc-index', data.index || '');

    const markUnavailable = () => {
      button.textContent = 'Sold Out';
      button.classList.add('suay-rcc-quick-add--disabled');
      button.setAttribute('aria-disabled', 'true');
      button.removeAttribute('data-quick-buy-handle');
      button.removeAttribute('data-quick-buy-product-id');
      button.removeAttribute('data-quick-buy-source');
      button.removeAttribute('data-quick-buy-list-id');
      button.removeAttribute('data-quick-buy-list-name');
      button.removeAttribute('data-quick-buy-index');
      button.removeAttribute('aria-controls');
      button.removeAttribute('aria-haspopup');
      button.setAttribute('aria-label', `Sold Out ${data.product.title}`);
    };

    if (isUnavailable) {
      button.textContent = 'Sold Out';
      button.classList.add('suay-rcc-quick-add--disabled');
      button.setAttribute('aria-disabled', 'true');
    } else {
      // Native cards never direct-add: a ?variant= in the product URL does
      // not prove the product is single-variant, so route through the
      // quick-buy modal (the custom-element path requires variants.length === 1)
      button.textContent = 'Choose Options';
      button.setAttribute('data-quick-buy-handle', data.handle);
      button.setAttribute('data-quick-buy-product-id', getProductId(data.product));
      button.setAttribute('data-quick-buy-source', 'collection');
      button.setAttribute('data-quick-buy-list-id', getCollectionListContext().id);
      button.setAttribute('data-quick-buy-list-name', getCollectionListContext().name);
      button.setAttribute('data-quick-buy-index', data.index || '');
      button.setAttribute('aria-controls', 'SuayQuickBuyOverlay');
      button.setAttribute('aria-haspopup', 'dialog');
    }

    button.setAttribute('aria-label', `${button.textContent} ${data.product.title}`);
    button.addEventListener('click', handleQuickActionClick);
    data.link.insertAdjacentElement('afterend', button);

    if (!isUnavailable) {
      // rcc-badges loads after this enhancer and renders its Out of Stock
      // label asynchronously — disable the quick action if it appears
      const badgeObserver = new MutationObserver(() => {
        if (!button.isConnected) {
          badgeObserver.disconnect();
          return;
        }
        if (card.querySelector('.product-label.unavailable')) {
          badgeObserver.disconnect();
          markUnavailable();
        }
      });
      badgeObserver.observe(card, { childList: true, subtree: true });
      window.setTimeout(() => badgeObserver.disconnect(), 15000);
    }

  };

  const enhanceNativeRccCards = (root) => {
    if (!isCollectionPage()) return;
    (root || document).querySelectorAll('.rcc-search__product').forEach(enhanceNativeRccCard);
  };

  const isPrimaryDtcHiddenProduct = (p) => {
    const title = p?.title || '';
    const tags = Array.isArray(p?.tags) ? p.tags : [];

    return /\|\s*blank\b/i.test(title) || tags.some((tag) => {
      const normalized = String(tag || '').trim().toLowerCase();
      return normalized === 'blank' || normalized === 'mystery' || normalized === 'hide_from_search';
    });
  };

  const equalAspectRatio = (a, b) => {
    if (!a || !b) return false;
    if (!a.width || !a.height || !b.width || !b.height) return false;
    const r1 = a.width / a.height;
    const r2 = b.width / b.height;
    return Math.abs(r1 - r2) < 0.01;
  };

  const responsiveImage = ({ url, width, height, alt, deferred = false }) => {
    const safeAlt = escapeHTML(alt || '');
    const w = Number(width) || 0;
    const h = Number(height) || 0;
    const paddingTop = w > 0 && h > 0 ? (h / w) * 100 : 100;
    const dimAttrs = w > 0 && h > 0 ? ` width="${w}" height="${h}"` : '';
    return `
      <div class="rimage-outer-wrapper">
        <div class="rimage-wrapper" style="padding-top:${paddingTop}%;">
          <img class="rimage__image" ${deferred ? `data-rcc-hover-src="${escapeHTML(url)}"` : `src="${escapeHTML(url)}"`} alt="${safeAlt}" loading="lazy" decoding="async"${dimAttrs} />
        </div>
      </div>
    `;
  };

  const hoverImageMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
  let hoverImageObserver = null;

  const loadHoverImage = (root) => {
    const image = root?.querySelector?.('img[data-rcc-hover-src]');
    if (!image) return;

    image.src = image.dataset.rccHoverSrc;
    image.removeAttribute('data-rcc-hover-src');
    hoverImageObserver?.unobserve(root);
  };

  const scheduleHoverImage = (card) => {
    if (!hoverImageMedia.matches || !card?.querySelector('img[data-rcc-hover-src]')) return;

    if (!('IntersectionObserver' in window)) return;
    if (!hoverImageObserver) {
      hoverImageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) loadHoverImage(entry.target);
        });
      }, { rootMargin: '600px 0px' });
    }
    hoverImageObserver.observe(card);
  };

  const productLabelMarkup = ({ onSale, soldOut }) => {
    if (soldOut) {
      return '<span class="product-label unavailable"><span>Sold out</span></span>';
    }
    if (onSale) {
      return '<span class="product-label sale"><span>Sale</span></span>';
    }
    return '';
  };

  const renderRating = (rating, href) => {
    if (!rating) return '';
    const avg = Number(rating.averageRating);
    const count = Number(rating.ratingCount) || 0;
    if (!Number.isFinite(avg) || count <= 0) return '';
    const rounded = Math.round(avg * 2) / 2; // nearest 0.5
    const decimal = rounded % 1 !== 0 ? 0.5 : 0;
    const activeCount = Math.ceil(rounded);
    const starSvg = () => (
      '<svg class="cc-rating-stars__svg" style="display:inline-block" width="13" height="13" viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M8.43579 4.9459L13.63 4.9536L9.42653 8.01929L11.0329 12.97L6.81981 9.91778L2.60673 12.97L4.20346 8.01929L0 4.9536L5.20383 4.9459L6.81981 0L8.43579 4.9459Z"/>' +
      '</svg>'
    );

    const inactive = Array.from({ length: 5 }).map(starSvg).join('');
    const active = Array.from({ length: activeCount }).map(starSvg).join('');

    const aria = `${rounded} / 5`;
    const countHtml = count > 0 ? `<span class="cc-rating-custom-caption">(${count})</span>` : '';

    return `
      <a href="${href}" class="themed-product-reviews" tabindex="-1">
        <div class="cc-rating" role="img" data-rating-decimal="${decimal}" aria-label="${escapeHTML(aria)}">
          <span class="cc-rating-stars">
            <span class="cc-rating-stars--inactive">${inactive}</span>
            <span class="cc-rating-stars--active">${active}</span>
          </span>
        </div>
        ${countHtml}
      </a>
    `;
  };

  // Custom Element
  class ProductCustomProduct extends HTMLElement {
    static get observedAttributes() {
      return ['show-vendor', 'product-class', 'loop-index'];
    }

    constructor() {
      super();
      this._product = null;
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    attributeChangedCallback() {
      if (this._product) this.render();
    }

    connectedCallback() {
      if (this._product) this.render();
    }

    render() {
      const p = this._product;
      if (!p) {
        this.innerHTML = '';
        return;
      }

      if (isPrimaryDtcHiddenProduct(p)) {
        this.hidden = true;
        this.classList.add('suay-rcc-product--hidden');
        this.innerHTML = '';
        return;
      }

      this.hidden = false;
      this.classList.remove('suay-rcc-product--hidden');

      const showVendorAttr = this.getAttribute('show-vendor');
      const showVendor = !(showVendorAttr === null || showVendorAttr === 'false');
      const extraClass = this.getAttribute('product-class') || '';
      const loopIndex = this.getAttribute('loop-index') || (p.index != null ? String(p.index) : '');

      const url = p.url || '#';
      const title = p.title || '';
      const brand = p.brand || '';
      const currencyCode = p?.priceInfo?.currencyCode || 'USD';

      const primary = p?.img
        ? { url: p.img.uri, width: p.img.width, height: p.img.height }
        : (Array.isArray(p?.images) && p.images[0]
            ? { url: p.images[0].url, width: p.images[0].width, height: p.images[0].height }
            : null);

      const secondary = p?.imgHover
        ? { url: p.imgHover.uri, width: p.imgHover.width, height: p.imgHover.height }
        : (Array.isArray(p?.images) && p.images[1]
            ? { url: p.images[1].url, width: p.images[1].width, height: p.images[1].height }
            : null);

      const { priceCents, originalCents, onSale } = pricesFromProduct(p);
      const soldOut = isSoldOut(p);
      const directAddVariant = getDirectAddVariant(p);
      const productHandle = productHandleFromUrl(url);
      const listContext = getCollectionListContext();
      const normalizedIndex = normalizeIndex(loopIndex || p.index, this);
      const sameAspect = equalAspectRatio(primary, secondary);

      const safeTitle = escapeHTML(title);
      const safeBrand = escapeHTML(brand);

      const labelHtml = productLabelMarkup({ onSale, soldOut });
      const quickActionHtml = (() => {
        if (!isCollectionRccGridElement(this)) return '';

        if (soldOut) {
          return `
            <span class="suay-rcc-quick-add suay-rcc-quick-add--disabled" aria-disabled="true">
              Sold Out
            </span>
          `;
        }

        if (directAddVariant?.id) {
          return `
            <button
              type="button"
              class="suay-rcc-quick-add"
              data-rcc-quick-add
              data-rcc-variant-id="${escapeHTML(directAddVariant.id)}"
              data-rcc-index="${escapeHTML(normalizedIndex || '')}"
              aria-label="Quick Add ${safeTitle}"
            >
              Quick Add
            </button>
          `;
        }

        if (!productHandle) return '';

        return `
          <button
            type="button"
            class="suay-rcc-quick-add"
            data-quick-buy-handle="${escapeHTML(productHandle)}"
            data-quick-buy-product-id="${escapeHTML(getProductId(p))}"
            data-quick-buy-source="collection"
            data-quick-buy-list-id="${escapeHTML(listContext.id)}"
            data-quick-buy-list-name="${escapeHTML(listContext.name)}"
            data-quick-buy-index="${escapeHTML(normalizedIndex || '')}"
            aria-controls="SuayQuickBuyOverlay"
            aria-haspopup="dialog"
            aria-label="Choose options for ${safeTitle}"
          >
            Choose Options
          </button>
        `;
      })();

      const priceHtml = (() => {
        const curr = typeof priceCents === 'number' ? formatMoney(priceCents, currencyCode) : '';
        const was = onSale && typeof originalCents === 'number' ? `<span class="was-price theme-money">${formatMoney(originalCents, currencyCode).replace('<span class="theme-money">','').replace('</span>','')}</span>` : '';
        return `<div class="price">${curr}${was ? ' ' + was : ''}</div>`;
      })();

      const vendorHtml = showVendor && safeBrand ? `<span class="vendor">${safeBrand}</span>` : '';

      const ratingHtml = renderRating(p.rating, url);

      const imageFirst = primary ? responsiveImage({ url: primary.url, width: primary.width, height: primary.height, alt: title }) : '';
      const imageSecond = secondary ? responsiveImage({ url: secondary.url, width: secondary.width, height: secondary.height, alt: title, deferred: true }) : '';

      const imageContainerClasses = ['image', 'image--hover-second'];
      if (sameAspect) imageContainerClasses.push('image--same-aspect-ratio');

      const outerClasses = [
        'product-block',
        'cc-product-block',
        'hover-image',
        extraClass
      ];
      if (soldOut) outerClasses.push('sold-out');
      if (onSale) outerClasses.push('on-sale');

      const loopAttr = loopIndex ? ` data-loop-index="${escapeHTML(loopIndex)}"` : '';

      this.innerHTML = `
        <div class="${outerClasses.filter(Boolean).join(' ')}"${loopAttr}>
          <div class="product-block__inner">
            <div class="${imageContainerClasses.join(' ')}">
              <a class="image-inner suay-rcc-product-link" href="${url}" aria-label="${safeTitle}" tabindex="-1">
                <div class="image__first">
                  ${imageFirst}
                  ${labelHtml}
                </div>
                ${secondary ? `<div class="image__second">${imageSecond}</div>` : ''}
              </a>
              ${quickActionHtml}
            </div>

            <a href="${url}" class="caption upper suay-rcc-product-link">
              <span class="title">${safeTitle}</span>
              ${vendorHtml}
              ${priceHtml}
            </a>

            ${ratingHtml}
          </div>
        </div>
      `;

      this.querySelectorAll('.suay-rcc-quick-add').forEach((action) => {
        action.addEventListener('click', handleQuickActionClick);
      });
      scheduleHoverImage(this);
    }
  }

  customElements.define('rcc-suay-product', ProductCustomProduct);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceNativeRccCards(document), { once: true });
  } else {
    enhanceNativeRccCards(document);
  }

  if (isCollectionPage()) {
    const nativeRccObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('.rcc-search__product')) {
            enhanceNativeRccCard(node);
            return;
          }

          const parentCard = node.closest('.rcc-search__product');
          if (parentCard) {
            // RCC re-rendered inside an already-enhanced card (filter/sort):
            // re-enhance if our action button was lost in the swap or now
            // points at a different product
            if (parentCard.dataset.suayRccEnhanced !== 'true') return;
            const existingButton = parentCard.querySelector('[data-rcc-native-quick-add]');
            const latest = nativeCardData(parentCard);
            const staleHandle =
              existingButton?.getAttribute('data-quick-buy-handle') &&
              latest?.handle &&
              existingButton.getAttribute('data-quick-buy-handle') !== latest.handle;
            if (!existingButton || staleHandle) {
              if (existingButton) existingButton.remove();
              delete parentCard.dataset.suayRccEnhanced;
              enhanceNativeRccCard(parentCard);
            }
            return;
          }

          enhanceNativeRccCards(node);
        });
      });
    });

    nativeRccObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  document.addEventListener('click', (event) => {
    const nativeButton = event.target.closest('[data-rcc-native-quick-add]');
    if (nativeButton) {
      // Choose Options buttons are handled by suay-quick-buy.js via their
      // data-quick-buy-handle attribute (which also tracks select_item);
      // Sold Out buttons are inert
      event.preventDefault();
      return;
    }

    const directButton = event.target.closest('[data-rcc-quick-add]');
    if (directButton) {
      event.preventDefault();
      trackSelectItem(directButton);
      addDirectVariantToCart(directButton);
      return;
    }

    const link = event.target.closest('.suay-rcc-product-link');
    if (link) {
      trackSelectItem(link);
      return;
    }

    const nativeLink = event.target.closest('.suay-rcc-native-product-link');
    if (nativeLink) {
      trackNativeSelectItem(nativeLink);
    }
  });

  document.addEventListener('pointerover', (event) => {
    if (!hoverImageMedia.matches) return;
    const card = event.target.closest('rcc-suay-product');
    if (card) loadHoverImage(card);
  }, { passive: true });

  document.addEventListener('focusin', (event) => {
    if (!hoverImageMedia.matches) return;
    const card = event.target.closest('rcc-suay-product');
    if (card) loadHoverImage(card);
  });
})();
